/**
 * nj-attach — mints the REAL record a journey document is about.
 *
 * The gap this closes (build plan, stage 3's disclosed residual, stage 5's
 * "open decision"): `+ New journey` created a `local:` key and nothing else, so
 * every Setup value on a designer-created journey stayed in the document and
 * never reached a record. The wizard was the only thing in the app that could
 * bring an event into existence. Operator ruling 2026-08-24: the designer must
 * be able to create the record for EVERY interaction type, not just edit ones
 * that already exist.
 *
 * One function per type, each calling the SAME op that type's own editor calls
 * — no new contract, no second creation path:
 *
 *   event       creation.createEvent          (the wizard's own Publish-step op)
 *   queue       createQueue                   (the queue wizard's op)
 *   board       saveMessageBoard              (the boards library's op)
 *   appointment createCatalogueLob            (+ existing bookable services)
 *   experience  — already has its own record (putExperience); an `exp:` key IS
 *                 the stored object, so there is nothing to attach. Included in
 *                 ATTACH_NOUN so the UI can say so rather than offering a
 *                 no-op button.
 *
 * The document is NOT rewritten here. `attachJourney` returns the new key and
 * origin; the caller re-keys the document (local:… → event:…) and keeps every
 * page, block and flow step exactly as authored. Held Setup values (`setup`,
 * `qsetup`, `boardCfg`) become the create payload, which is what makes the
 * attach lossless — the fields the author already filled in are the record's
 * opening values, not defaults.
 */
import { creation, createLandingPage, updateLandingPage, publishLandingPage, apiRaw } from "@v0y4ge/api-client";
import { studioRequestOptions } from "../../lib/studio-config";
import { createQueue, saveMessageBoard } from "../queues/queues-api";
import { createCatalogueLob, updateCatalogueService } from "../workforce/wf-api";
import { listOneStopPages, saveOneStopDraft, slugFromTitle, uniqueSlug } from "./onestop-page-api";
import { isPublicAnyone } from "../events/creation-store";
import { refetchEvents } from "../events/useEvents";
import { tzContext } from "../../lib/datetime";
import type { Doc, Origin, NJPage, Block } from "./nj-model";
import { t as i18nT } from "../../lib/i18n";
import { readAudience } from "../../lib/audience";
import { allDayEnd, sendableEnd } from "./nj-binding";

export interface AttachResult {
  /** The journey's new key — `event:<id>`, `queue:<id>`, … */
  key: string;
  origin: Origin;
  /** Human sentence for the toast: what now exists. */
  note: string;
  /** The journey document re-keyed onto its new owner. Set by the designer's attach step (not by attachJourney itself),
   *  so a caller that goes on to write the document writes THIS one under `key`, never the stale pre-attach key. */
  doc?: Doc;
}

/** What each journey type attaches TO, for UI copy. */
// Keys are journey types (identities); each value is a getter, so it is read in the viewer's language
// when the copy renders.
export const ATTACH_NOUN: Record<string, string> = {
  get Event() { return i18nT("journeys.njAttach.noun.event", "event"); },
  get Queue() { return i18nT("journeys.njAttach.noun.queue", "queue"); },
  get Board() { return i18nT("journeys.njAttach.noun.board", "display board"); },
  get Appointment() { return i18nT("journeys.njAttach.noun.appointment", "line of business"); },
  get "One-Stop"() { return i18nT("journeys.njAttach.noun.oneStop", "One-Stop page"); },
  get Experience() { return i18nT("journeys.njAttach.noun.experience", "experience"); },
};

/** Types the designer can mint a record for. Experiences are excluded because
 *  an `exp:` journey already IS its record. */
export const CAN_ATTACH = new Set(["Event", "Queue", "Board", "Appointment", "One-Stop"]); // ui-config: journey type identities

/** True when this document has no owning record yet but could have one. */
export function needsAttach(doc: Doc | null | undefined): boolean {
  if (!doc) return false;
  const kind = doc.origin?.kind;
  if (kind && kind !== "local") return false;
  return CAN_ATTACH.has(doc.type ?? "");
}

/** Fields the author must have filled before the record can be created.
 *  Deliberately minimal — the record's OWN readiness gate gates publishing;
 *  this only covers what the create op itself requires. */
/** The journey's effective name: the document's, or the title held in Setup if
 *  the document still carries a template placeholder. */
export /**
 * The name this journey will give its record.
 *
 * A journey has up to THREE names and they are not the same field: the document's
 * own `name` (the designer header, "Untitled queue journey" until renamed), an
 * event's held `setup.title`, and a queue's held `qsetup.name` — which is what
 * the Setup tab's "Queue name" input writes.
 *
 * `qsetup.name` was missing here (operator, 2026-09-02: "it says I haven't given
 * it a name. I clearly have"). A queue named only in Setup — the normal path,
 * since that field is the required one with the asterisk — still read as untitled,
 * so Create the queue refused a queue that was correctly filled in.
 */
function effectiveName(doc: Doc): string {
  const held = String(
    (doc.setup as { title?: unknown } | undefined)?.title
    ?? (doc.qsetup as { name?: unknown } | undefined)?.name
    ?? "",
  ).trim();
  const own = (doc.name || "").trim();
  return !own || /^untitled/i.test(own) ? (held || own) : own;
}

export function attachBlockers(doc: Doc): string[] {
  const out: string[] = [];
  const name = effectiveName(doc);
  if (!name || /^untitled/i.test(name)) out.push(i18nT("journeys.njAttach.blocker.name", "a name"));
  if (doc.type === "Queue") {
    const q = doc.qsetup ?? {};
    /* No `typeId` blocker (2026-09-02). The Setup tab's queue-type picker was
       removed — a queue journey's pages arrive with it — so there is no longer
       anywhere to answer it. Asking for a value the UI cannot supply blocks
       creation with a reason nobody can act on, which is worse than the default
       `typeId` the record already carries. */
    if (!q.locationId) out.push(i18nT("journeys.njAttach.blocker.location", "a location"));
  }
  return out;
}

/* ── Landing envelope ──────────────────────────────────────────────────
 * Stage 5's residual: publish flipped the record live but nothing wrote this
 * document's pages into the surface the public page serves, so a designed page
 * was never served. The landing contract is `{uid, type, cfg}[]` (LandingBlock,
 * landing-builder-helpers) persisted to landing_pages.blocks_json — so the
 * designer's blocks map onto it directly. Types the landing renderer doesn't
 * have (divider/spacer/eventdetails) map onto their nearest real block rather
 * than being dropped silently; the mapping is listed field by field so a
 * reviewer can check it against LANDING_BLOCKS. */

export interface LandingBlockOut { uid: string; type: string; cfg: Record<string, unknown> }

const str = (v: unknown, d = ""): string => (typeof v === "string" ? v : d);
const bool = (v: unknown, d = false): boolean => (typeof v === "boolean" ? v : d);

/** The record values pages READ rather than author — the event's schedule and
 *  venue. Threaded into the landing mapping so a published page carries the same
 *  date and venue the designer showed. Without it `eventdetails` published as an
 *  EMPTY date strip and `countdown` as a target-less timer, so everything the
 *  operator entered in Setup · Schedule was lost at the public surface. */
export interface SetupFacts { start?: string; end?: string; allDay?: boolean; timezone?: string; venue?: string; address?: string }

/** Fallback facts read off the journey's own `setup` bag — what a journey with
 *  no event record yet carries. A caller holding the live record passes it in. */
function factsFromDoc(doc: Doc): SetupFacts {
  const s = doc.setup ?? {};
  return { start: s.start, end: s.end, allDay: s.allDay === true, timezone: s.timezone, venue: s.venueName, address: s.address };
}

/** Setup's date/time/venue as the three strings the landing date strip holds.
 *  Same shape the designer's Event details block renders, so the two agree. */
export function setupDateStrip(f: SetupFacts): { date: string; time: string; loc: string } {
  const at = (v?: string): Date | null => { if (!v) return null; const d = new Date(v); return Number.isNaN(d.getTime()) ? null : d; };
  const s = at(f.start);
  const e = at(f.end);
  const same = !!s && !!e && s.toDateString() === e.toDateString();
  const DF = { weekday: "short", day: "numeric", month: "long", year: "numeric" } as const;
  const TF = { hour: "numeric", minute: "2-digit" } as const;
  return {
    date: !s ? "" : (!e || same) ? s.toLocaleDateString(undefined, DF) : `${s.toLocaleDateString(undefined, DF)} — ${e.toLocaleDateString(undefined, DF)}`,
    time: !s ? "" : f.allDay ? i18nT("journeys.njAttach.allDay", "All day") : `${s.toLocaleTimeString(undefined, TF)}${e ? ` – ${e.toLocaleTimeString(undefined, TF)}` : ""}`,
    loc: [f.venue, f.address].filter(Boolean).join(" · "),
  };
}

/** One designer block → one landing block. Returns null for blocks with no
 *  landing representation at all (never silently lossy: the caller counts them
 *  and the publish summary reports the count). */
function landingBlock(b: Block, page: NJPage, f: SetupFacts): LandingBlockOut | null {
  const uid = str(b.uid) || `lb_${Math.random().toString(36).slice(2, 9)}`;
  const t = b.type;
  const cfg = (k: string, d?: unknown) => (b[k] === undefined ? d : b[k]);
  switch (t) {
    case "text":
      return { uid, type: "text", cfg: { heading: "", body: str(b.text), align: String(cfg("align", "Left")).toLowerCase() } };
    case "notice":
      // No notice block on the landing side — a notice IS a text block with a
      // tone, and the landing text block is the honest carrier for the copy.
      return { uid, type: "text", cfg: { heading: "", body: str(b.text), align: "left" } };
    case "hero":
      return { uid, type: "hero", cfg: {
        headline: str(b.text) || page.h, subhead: str(cfg("sub")),
        bgKind: String(cfg("bg", "Gradient")).toLowerCase() === "image" ? "image" : "gradient",
        imageUrl: str(cfg("img")), padding: 40, align: "left",
        showCTA: !!str(cfg("cta")), ctaLabel: str(cfg("cta"), i18nT("journeys.njAttach.register", "Register")),
      } };
    case "image":
      return { uid, type: "image", cfg: { imageUrl: str(cfg("src")), caption: str(cfg("caption")), rounded: bool(cfg("round"), true), maxW: 0 } };
    case "video":
      return { uid, type: "video", cfg: { url: str(cfg("url")), caption: str(cfg("caption")) } };
    case "gallery":
      return { uid, type: "gallery", cfg: { title: "", images: str(cfg("urls")).split(/[\s,]+/).filter(Boolean), cols: Number(cfg("count", 3)) || 3, motion: "grid" } };
    case "map":
      // Falls back to the venue Setup holds, matching the designer's map block.
      return { uid, type: "map", cfg: { title: i18nT("journeys.njAttach.findUs", "Find us"), height: 240, address: str(cfg("address")) || f.address || f.venue || "" } };
    case "faq":
      return { uid, type: "faq", cfg: { title: i18nT("journeys.njAttach.faq", "FAQ"), items: [
        { q: str(b.text), a: str(cfg("a1")) },
        { q: str(cfg("text2")), a: str(cfg("a2")) },
      ].filter((r) => r.q) } };
    case "quote":
      return { uid, type: "quote", cfg: { quote: str(b.text), name: str(cfg("who")), role: "", photo: "" } };
    case "stats":
      return { uid, type: "stats", cfg: { items: [1, 2, 3]
        .map((n) => ({ value: str(cfg(`s${n}v`)), label: str(cfg(`s${n}l`)) }))
        .filter((r) => r.value || r.label) } };
    case "countdown":
      // Counts down to the event's real start, not to nothing.
      return { uid, type: "countdown", cfg: { title: str(cfg("label"), i18nT("journeys.njAttach.startsIn", "Starts in")), target: f.start ?? "" } };
    case "social": {
      const links: Record<string, string> = {};
      (["x", "fb", "ig", "li", "yt", "tt", "wa", "th"] as const).forEach((k) => {
        // ui-config: `${k}Url` is the block's config key for that network
        if (cfg(k) && str(cfg(`${k}Url`))) links[k] = str(cfg(`${k}Url`));
      });
      return { uid, type: "social", cfg: { title: i18nT("journeys.njAttach.followAlong", "Follow along"), smode: "follow", links } };
    }
    case "button":
      // The landing palette has no standalone button; a register CTA is what
      // one nearly always is on an event page, and signup carries a label.
      return { uid, type: "signup", cfg: { title: "", cta: str(b.text, i18nT("journeys.njAttach.register", "Register")), showName: true, showCo: false } };
    case "eventdetails": {
      // Reads the record, exactly as the designer's block does. Toggles decide
      // which pieces travel; the date strip is empty only when Setup is.
      const strip = setupDateStrip(f);
      const wantDate = bool(cfg("showDate", true), true);
      const wantLoc = bool(cfg("showLocation", true), true);
      return { uid, type: "dates", cfg: {
        date: wantDate ? strip.date : "",
        time: wantDate ? strip.time : "",
        loc: wantLoc ? strip.loc : "",
        style: String(cfg("layout", "Inline")) === "Card" ? "card" : "inline",
      } };
    }
    default:
      return null; // divider, spacer — presentation-only, no landing equivalent
  }
}

/** The core block each page type contributes to the public surface. */
const CORE_LANDING: Record<string, (p: NJPage) => LandingBlockOut | null> = {
  register: (p) => ({ uid: `core_${p.uid ?? p.id}`, type: "signup", cfg: { title: p.h || i18nT("journeys.njAttach.register", "Register"), cta: i18nT("journeys.njAttach.register", "Register"), showName: true, showCo: false } }),
  tickets: (p) => ({ uid: `core_${p.uid ?? p.id}`, type: "tickets", cfg: { title: p.h || i18nT("journeys.njAttach.tickets", "Tickets"), layout: "list", showSold: false } }),
};

export interface LandingEnvelope {
  blocks: LandingBlockOut[];
  theme: string;
  font: string;
  slug: string;
  title: string;
  /** Blocks with no landing representation, by type — reported, never hidden. */
  dropped: string[];
}

export const slugify = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/** Map the journey's ENTRY page (plus the pages that follow it) onto the
 *  landing envelope the public renderer reads. */
export function landingFromDoc(doc: Doc, setup?: SetupFacts): LandingEnvelope {
  const facts = setup ?? factsFromDoc(doc);
  const dropped: string[] = [];
  const blocks: LandingBlockOut[] = [];
  const entryIdx = Math.max(0, doc.pages.findIndex((p) => (p.uid ?? p.id) === doc.entryUid));
  const ordered = [doc.pages[entryIdx], ...doc.pages.filter((_, i) => i !== entryIdx)].filter(Boolean) as NJPage[];
  ordered.forEach((p, i) => {
    // The entry page contributes its content; later pages contribute their core
    // (the register form, the ticket picker) so a one-page public surface still
    // lets someone register — which is what the landing envelope serves.
    if (i === 0) {
      p.content.forEach((b) => {
        const out = landingBlock(b, p, facts);
        if (out) blocks.push(out); else dropped.push(String(b.type));
      });
    }
    const core = CORE_LANDING[p.id];
    if (core) { const out = core(p); if (out && !blocks.some((x) => x.type === out.type)) blocks.push(out); }
  });
  const pub = doc.publish ?? {};
  return {
    blocks,
    theme: pub.theme || "Brand", // ui-config: stored theme id
    font: pub.font || "DM Sans", // ui-config: stored font id
    slug: pub.slug || slugify(doc.name || "") || "untitled",
    title: doc.name || "Untitled", // ui-config: placeholder the /^untitled/i checks match
    dropped: [...new Set(dropped)],
  };
}

export interface PublishPagesResult { landingPageId: string; published: boolean; dropped: string[] }

/**
 * Write the journey's pages to the public surface, then publish it.
 * `landingPageId` is remembered by the caller (Doc.publish.landingPageId) so the
 * second publish updates the same page instead of minting another.
 */
export async function publishJourneyPages(
  eventId: string,
  doc: Doc,
  existingPageId?: string | null,
  publish = true,
  setup?: SetupFacts,
): Promise<PublishPagesResult> {
  const env = landingFromDoc(doc, setup);
  const opts = studioRequestOptions("landing-pages");
  const input = {
    eventId,
    slug: env.slug,
    title: env.title,
    status: (publish ? "published" : "draft") as "published" | "draft",
    blocks: env.blocks,
    theme: env.theme,
    font: env.font,
    globalCss: doc.publish?.customCss || "",
  };
  const saved = existingPageId
    ? await updateLandingPage(existingPageId, input as Parameters<typeof updateLandingPage>[1], opts)
    : await createLandingPage(input as Parameters<typeof createLandingPage>[0], opts);
  const id = String((saved as unknown as { id?: string } | null)?.id || existingPageId || "");
  if (publish && id) await publishLandingPage(id, opts, { status: "published" });
  return { landingPageId: id, published: publish, dropped: env.dropped };
}

/* ── Attach: mint the record ───────────────────────────────────────────── */

const canonicalVisibility = (v?: string | null): "public" | "private" | undefined =>
  !v ? undefined : (v === "public" || isPublicAnyone(v)) ? "public" : "private";
const stripTzPrefix = (v?: string): string | undefined => (v ? v.replace(/^\(GMT[+-]?\d{1,2}(:\d{2})?\)\s*/, "") : v);

/** Setup's `method` ({inPerson, virtual}) → the contract's `format` string.
 *  The create input has no `method` field; sending the object left `format`
 *  unset on the real worker and made the harness echo an object into it.
 *  "in-person" is the stored spelling (the backend's default, the template check and every
 *  seeded event); "in_person" was this file's outlier and made an unchanged in-person event
 *  look changed on save (CC, 2026-10-06). */
export function formatFromMethod(m: unknown): "in-person" | "virtual" | "hybrid" | undefined {
  if (!m || typeof m !== "object") {
    if (typeof m !== "string" || !m) return undefined;
    return (/^in[\s_-]?person$/i.test(m.trim()) ? "in-person" : m) as "in-person";
  }
  const { inPerson, virtual } = m as { inPerson?: boolean; virtual?: boolean };
  return inPerson && virtual ? "hybrid" : virtual ? "virtual" : inPerson ? "in-person" : undefined;
}

const TICKET_KINDS = new Set(["free", "paid", "donation", "invite"]); // ui-config: EventTicketTypeConfigEntry.kind enum

/** EventTicketTypeConfigEntry requires qty + kind + price. The Tickets page
 *  only holds `qty`, so kind and price come from the selected library type —
 *  the same fallback the page already renders (`cfg.kind ?? t.kind`). */
export function completeTicketTypeConfigs(
  ids: string[] | undefined,
  cfgs: Record<string, { qty?: number; kind?: string; price?: number }> | undefined,
  types: ReadonlyArray<{ id: string; kind?: string | null; price?: number | string | null }>,
): Record<string, { qty: number; kind: "free" | "paid" | "donation" | "invite"; price: number }> | undefined {
  if (!ids?.length) return undefined;
  const out: Record<string, { qty: number; kind: "free" | "paid" | "donation" | "invite"; price: number }> = {};
  for (const id of ids) {
    const c = cfgs?.[id] ?? {};
    const t = types.find((x) => x.id === id);
    const price = Math.max(0, Number(c.price ?? t?.price ?? 0) || 0);
    const rawKind = String(c.kind ?? t?.kind ?? "").toLowerCase().replace(/[-_ ]?only$/, "");
    const kind = (TICKET_KINDS.has(rawKind) ? rawKind : price > 0 ? "paid" : "free") as "free" | "paid" | "donation" | "invite";
    out[id] = { qty: Math.max(0, Math.floor(Number(c.qty) || 0)), kind, price: kind === "free" || kind === "invite" ? 0 : price };
  }
  return out;
}

export interface AttachContext {
  ticketTypes?: ReadonlyArray<{ id: string; kind?: string | null; price?: number | string | null }>;
  /** The event id this draft's creates carry (CC 0277): minted once per draft by mintEventId and
   *  sent on every attempt, so a retry can only finish the first attempt, never make a second event. */
  eventId?: string;
}

const CROCKFORD = "0123456789abcdefghjkmnpqrstvwxyz";
/** A new event id in the server's own form: "evt-" + a lowercase ULID (48-bit time, 80 random bits). */
export function mintEventId(now: number = Date.now()): string {
  let time = "";
  for (let i = 0, n = now; i < 10; i++) { time = CROCKFORD[n % 32] + time; n = Math.floor(n / 32); }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let rand = "";
  for (let i = 0; i < 16; i++) rand += CROCKFORD[bytes[i] % 32];
  return "evt-" + time + rand;
}

/** Event ids whose save has resolved in this session (CC 0291). An id on the screen's document is not
 *  proof it was stored: a failed save leaves it in the on-screen overlay. */
const HELD_EVENT_IDS = new Set<string>();

/** The event id a draft's creates carry (CC 0277/0285/0291). A draft's id is reused, never replaced; a new
 *  one is minted only when the draft has none. The id counts as held only once a save of it has resolved
 *  in this session, so an id that reached the document through a failed save, or one read back after a
 *  reload, is saved again (same id) before this resolves. The create is sent only after that. If
 *  persist() rejects, so does this, and the caller sends no create. */
export async function holdEventId<D extends { pendingEventId?: string }>(
  doc: D, persist: (next: D) => Promise<void>, mint: () => string = mintEventId, held: Set<string> = HELD_EVENT_IDS,
): Promise<{ id: string; doc: D }> {
  const id = doc.pendingEventId || mint();
  const next = doc.pendingEventId ? doc : { ...doc, pendingEventId: id };
  if (!held.has(id)) {
    await persist(next);
    held.add(id);
  }
  return { id, doc: next };
}

const errCode = (e: unknown): string => {
  const x = (e ?? {}) as { code?: unknown; body?: { error?: { code?: unknown } } };
  return String(x.code ?? x.body?.error?.code ?? "");
};
const errStatus = (e: unknown): number => Number((e as { status?: unknown } | null)?.status) || 0;

/** How a failed create should be described (CC 0277/0278).
 *  - refused: the server turned the request down before writing (a 4xx other than 408 / 429), so nothing changed.
 *  - unconfirmed: a 5xx, a timeout, a dropped connection or SERVICE_WARMING; the create may have committed.
 *  - not-confirmed: the draft's event id is taken and the event can't be read back; retrying won't help. */
export function createFailureKind(e: unknown): "refused" | "unconfirmed" | "not-confirmed" {
  if (errCode(e) === "EVENT_NOT_CONFIRMED") return "not-confirmed";
  if (errCode(e) === "SERVICE_WARMING") return "unconfirmed";
  const status = errStatus(e);
  return status >= 400 && status < 500 && status !== 408 && status !== 429 ? "refused" : "unconfirmed";
}

async function attachEvent(doc: Doc, ctx: AttachContext = {}): Promise<AttachResult> {
  const s = doc.setup ?? {};
  const audience = readAudience((s as { audience?: unknown }).audience);
  const loc = s as { venueName?: string | null; locationId?: string | null; locationMode?: string | null };
  const env = landingFromDoc(doc);
  // Same shape as the wizard's buildPayload, restricted to what Setup actually
  // collects. Created as a DRAFT (the create input's status enum carries it): the
  // designer's Publish creates first and then publishes, so an event is never live
  // before the author pressed Publish (CC, 2026-10-06).
  const zone = stripTzPrefix(s.timezone) || tzContext().zone;
  const payload = {
    title: effectiveName(doc) || "Untitled event", // ui-config: stored placeholder name, matched by /^untitled/i
    type: "blank",
    status: "draft",
    description: s.description || undefined,
    visibility: canonicalVisibility(s.visibility) ?? "public",
    format: formatFromMethod(s.method),
    startsAt: s.start || undefined,
    // An all-day event ends at the end of its last day in its zone (a single day sent start == end); a
    // same-date end earlier than the start is the next morning (sendableEnd).
    endsAt: (s.allDay ? allDayEnd(s.end || s.start, zone) : sendableEnd(s.start, s.end, zone)) || undefined,
    // No zone picked: the workspace's (tenant) zone, never the worker's own default.
    timezone: zone,
    locationAddress: s.address || undefined,
    // Setup keeps the join link as virtualProvider and the cover as heroImage; the worker reads these names.
    meetingLink: s.virtualProvider || undefined,
    imageUrl: s.heroImage || undefined,
    // The venue and the saved location the author picked in Setup. The contract's create input does not
    // name locationName yet (the payload is cast below); the server reads both.
    locationName: loc.venueName || undefined,
    locationId: (loc.locationMode ?? "saved") !== "custom" ? (loc.locationId || undefined) : undefined,
    capacity: s.capacity ?? undefined,
    tags: s.tags?.length ? s.tags : undefined,
    ticketTypeIds: s.ticketTypeIds ?? undefined,
    ticketTypeConfigs: completeTicketTypeConfigs(s.ticketTypeIds, s.ticketTypeConfigs, ctx.ticketTypes ?? []),
    ...(audience ? { audience } : {}),
    // The doc's question group becomes the event's booking questions (CC 0030 item 17).
    ...((doc as { attach?: { groupId?: string | null } }).attach?.groupId ? { bookingQuestions: [{ kind: "group" as const, id: String((doc as { attach?: { groupId?: string | null } }).attach?.groupId) }] } : {}),
    landing: { slug: env.slug, useLanding: true, landingDesign: { blocks: env.blocks, theme: env.theme, font: env.font } },
    /* The caller-chosen id (CC 0277): the server checks it against the event rows before writing. */
    ...(ctx.eventId ? { eventId: ctx.eventId } : {}),
  };
  let res: Record<string, unknown>;
  try {
    res = await creation.createEvent(payload as unknown as Parameters<typeof creation.createEvent>[0]) as Record<string, unknown>;
  } catch (e) {
    if (!ctx.eventId || errCode(e) !== "EVENT_ID_CONFLICT") throw e;
    /* 409 on this draft's own id: an earlier attempt did create the event. Read it and carry on
       exactly as after a successful create. A 404 means the id isn't ours to adopt. */
    try {
      res = await apiRaw<Record<string, unknown>>(`/v1/admin/events/${encodeURIComponent(ctx.eventId)}`, undefined, "getEvent");
    } catch (r) {
      if (errStatus(r) === 404) throw Object.assign(new Error("an event with this draft's id exists but could not be found"), { code: "EVENT_NOT_CONFIRMED" });
      throw r;
    }
    res = { id: ctx.eventId, ...(res ?? {}) };
  }
  const id = String(res?.id ?? (res?.event as { id?: string } | undefined)?.id ?? (res?.data as { id?: string } | undefined)?.id ?? "");
  if (!id) throw new Error(i18nT("journeys.njAttach.error.eventNoId", "the event was created but returned no id"));
  /* The events list only re-reads on its own inside a freshness window, so it would keep showing
     the list from before this create until a reload. Re-read it now, as every other event write does. */
  void refetchEvents();
  return {
    key: `event:${id}`,
    origin: { kind: "event", id, label: effectiveName(doc) || "Untitled event", route: "events", slug: env.slug }, // ui-config: stored placeholder name
    note: i18nT("journeys.njAttach.note.eventCreated", "Event created — Setup now saves onto the real event record"),
  };
}

async function attachQueue(doc: Doc): Promise<AttachResult> {
  const q = doc.qsetup ?? {};
  const created = await createQueue({
    name: doc.name || "Untitled queue", // ui-config: stored placeholder name, matched by /^untitled/i
    locationId: String(q.locationId || ""),
    locationName: q.locationName,
    typeId: q.typeId,
    ticketPrefix: q.ticketPrefix,
    slaTargetMins: q.slaTargetMins,
    services: q.services,
  });
  const id = String((created as unknown as { id?: string } | null)?.id || "");
  if (!id) throw new Error(i18nT("journeys.njAttach.error.queueNoId", "the queue was created but returned no id"));
  return {
    key: `queue:${id}`,
    origin: { kind: "queue", id, label: doc.name || "Untitled queue", route: "queues.console", params: { id } }, // ui-config: stored placeholder name
    note: i18nT("journeys.njAttach.note.queueCreated", "Queue created — it shows in the queues list and Setup saves onto it"),
  };
}

async function attachBoard(doc: Doc): Promise<AttachResult> {
  const c = doc.boardCfg ?? {};
  // Reuse the id the held configuration already carries, so a board saved from
  // the designer is the SAME board the journey's configuration was authored on.
  const id = String(c.id || `mb_${Date.now().toString(36)}`);
  await saveMessageBoard({
    id,
    name: doc.name || "Untitled display board", // ui-config: stored placeholder name, matched by /^untitled/i
    queueIds: c.queueIds ?? [],
    locationId: c.locationId ?? null,
    layout: c.layout ?? "full-list",
    theme: c.theme ?? "light",
    showWaitEstimates: c.showWaitEstimates ?? true,
    nameFormat: c.nameFormat ?? "first-initial",
    message: c.message ?? "",
    blocks: c.blocks ?? [],
  });
  return {
    key: `board:${id}`,
    origin: { kind: "board", id, label: doc.name || "Untitled display board", route: "queues.boards" }, // ui-config: stored placeholder name
    note: i18nT("journeys.njAttach.note.boardSaved", "Display board saved to the boards library"),
  };
}

async function attachAppointment(doc: Doc): Promise<AttachResult> {
  // An appointment journey is a line-of-business grouping of bookable catalogue
  // services (docFromApptGroup). Creating one means creating the LOB, then
  // ASSIGNING the services the author picked on the journey's service page —
  // service→LOB membership lives on the service, so the LOB alone is an empty
  // grouping and the journey comes back reading "0 bookable services".
  //
  // The catalogue keys a LOB by its NAME, not by an id: `ServiceDraft.lob`,
  // `OpsServiceCategory.lob` and `createCatalogueIntent({name, lob})` all carry
  // the name, and `createCatalogueLob` returns the whole catalogue rather than
  // the new row. So the name is what gets written onto each service, and the
  // slug is only this journey's local key.
  const lobName = (doc.name || "Untitled booking journey").trim(); // ui-config: the catalogue keys a line of business by this name
  await createCatalogueLob({ name: lobName, description: doc.sub || null });
  const id = slugify(lobName) || "booking";

  const picked = [...new Set(
    (doc.pages.find((p) => p.id === "apptsvc")?.svc ?? [])
      .map((s) => String(s).trim())
      .filter((s) => s.length > 0),
  )];
  let assigned = 0;
  const failed: string[] = [];
  for (const serviceId of picked) {
    /* One at a time, and a failure is COUNTED rather than thrown: the LOB
       already exists by this point, so throwing would leave a created record
       the caller believes was never made. The note says what actually happened. */
    try { await updateCatalogueService(serviceId, { lob: lobName }); assigned += 1; }
    catch { failed.push(serviceId); }
  }

  const note = picked.length === 0
    ? i18nT("journeys.njAttach.note.lobCreatedPick", "Line of business created — pick its bookable services on the Services page")
    : failed.length === 0
      ? i18nT("journeys.njAttach.note.lobCreatedWith", "{count, plural, one {Line of business created with # bookable service} other {Line of business created with # bookable services}}", { count: assigned })
      : i18nT("journeys.njAttach.note.lobCreatedPartial", "{assigned, plural, one {Line of business created — # service assigned, {refused} the catalogue refused} other {Line of business created — # services assigned, {refused} the catalogue refused}}", { assigned, refused: failed.length });

  return {
    key: `appt:${id}`,
    origin: { kind: "appointment", id, label: lobName, route: "ops.catalogue" },
    note,
  };
}

/**
 * A One-Stop journey's record is its PORTAL PAGE — a slug the student-facing
 * portal can serve. Until it exists the journey is a document like any other
 * unattached one: designed, saved, and reaching nobody.
 *
 * Creating it saves a DRAFT, deliberately. Attach is "this record now exists",
 * not "this is live" — the same promise it makes for an event (created, not
 * published) and a queue (created, not open). Publish is its own act.
 */
async function attachOneStop(doc: Doc): Promise<AttachResult> {
  const page = doc.pages.find((p) => p.id === "onestop") ?? doc.pages[0];
  const settings = page?.osSettings ?? {};
  const title = effectiveName(doc) || "One-Stop"; // ui-config: becomes the page slug

  /* The slug is the URL and is immutable once saved, so it is minted from the
     title and de-duped against the pages that already exist rather than assumed
     unique.
 
     THE LIST FAILURE IS FATAL, and swallowing it was a data-loss bug (fixed
     2026-09-08). `saveOneStopDraft` UPSERTS by slug. So `.catch(() => [])`
     turned "we could not read the existing pages" into "there are no existing
     pages", the de-dupe then found no collision, and a title matching a live
     page OVERWROTE that page's draft while reporting "One-Stop page created".
     A refused read must not be able to destroy a record.
 
     Paging is a REAL residual and is not faked here: `listPortalExperiencePages`
     takes no page argument, so a tenant past the server's first page has slugs
     this de-dupe cannot see. Passing a `{ page }` the op does not declare would
     read as handled while doing nothing — the declared-and-unread shape. Raised
     with the API line instead; until the op pages, the upsert-overwrite risk is
     bounded by the fatal-read fix above rather than eliminated. */
  let taken: string[];
  try {
    taken = (await listOneStopPages()).map((r) => r.slug);
  } catch (e) {
    throw new Error(
      i18nT("journeys.njAttach.error.slugCheckFailed", "couldn't check which One-Stop page addresses are already in use ({error}), so the page wasn't created — creating one blind could overwrite an existing page", { error: e instanceof Error ? e.message : String(e) }),
    );
  }
  const slug = settings.slug || uniqueSlug(slugFromTitle(title), taken);

  const blocks = (page?.osBlocks ?? []).map((b) => ({
    id: b.id, type: b.type, version: b.version,
    ...(b.visibility && b.visibility !== "always" ? { visibility: b.visibility } : {}),
    config: b.config ?? {},
  }));

  const saved = await saveOneStopDraft(slug, {
    title,
    header: { eyebrow: null, title, subtitle: page?.s || null },
    blocks: blocks as never,
    settings: {
      navLabel: settings.navLabel ?? null,
      locale: settings.locale ?? "en-US",
      supportedLocales: settings.supportedLocales ?? [settings.locale ?? "en-US"],
      /* NOT `?? "UTC"`. Every civil date and opening time on the page is read in
         this zone, so a guessed one publishes the wrong hour to students — which
         is exactly why `emptyOneStopDocument` declares `timeZone` required with
         no default. The tenant's zone is available here; UTC was a guess wearing
         a default's clothes (fixed 2026-09-08). */
      timeZone: settings.timeZone ?? tzContext().zone,
      /* layout / showHeader / showFooter are required on the settings object
         (JAI-1473) and were omitted here. Contract defaults, spelled out rather
         than left to the schema: this object is typed as the OUTPUT shape, where
         a defaulted field is present, so omitting it is a type error and not a
         "let the server decide" (2026-09-08). */
      layout: "get-help-first",
      showHeader: true,
      showFooter: true,
    },
  });

  return {
    key: `onestop:${saved.slug}`,
    origin: { kind: "onestop", id: saved.slug, label: title, route: "journeys", slug: saved.slug },
    note: i18nT("journeys.njAttach.note.oneStopCreated", "One-Stop page created at onestop/{slug} — it's a draft, so nothing is public yet", { slug: saved.slug }),
  };
}

/** Mint the record this journey is about. Throws with a plain message on
 *  failure; the caller keeps the document untouched and says so. */
export async function attachJourney(doc: Doc, ctx: AttachContext = {}): Promise<AttachResult> {
  switch (doc.type) {
    case "Event": return attachEvent(doc, ctx);
    case "Queue": return attachQueue(doc);
    case "Board": return attachBoard(doc);
    case "Appointment": return attachAppointment(doc);
    case "One-Stop": return attachOneStop(doc);
    default: throw new Error(doc.type
      ? i18nT("journeys.njAttach.error.noRecordType", "{type} has no record type to create", { type: doc.type })
      : i18nT("journeys.njAttach.error.noRecordTypeUntyped", "This journey has no record type to create"));
  }
}
